import { type FC, useCallback, useState } from 'react';
import { type StudyApiId } from '@/model/Study.model';
import {
  type ReservedSample,
  type ReservedSampleApiId,
  type GroupMap,
  type SampleTypeMap,
  type MetaField,
  type SampleDefinitionMap,
} from '../SampleNames.model';
import { toggleSetIds } from '../SampleNames.utils';
import { errorToast, successToast } from '@/helpers';
import Button from '@/components/UI/Button/Button';
import { ModalContainer, ModalHeader } from '@/utils/modal';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import Banner from '@/components/UI/Banner/Banner';
import ReservedSamplesFlatTable from '../ReservedSamplesFlatTable';

interface CreateReservedPlannedSamplesModalProps {
  studyApiId: StudyApiId;
  reservedSampleApiIds: Array<ReservedSampleApiId>;
  groupMap: GroupMap;
  sampleTypeMap: SampleTypeMap;
  metaFields: Array<MetaField>;
  sampleDefinitionMap: SampleDefinitionMap;
  closeModal: () => void;
  onSuccess: () => void;
}

export const CreateReservedPlannedSamplesModal: FC<CreateReservedPlannedSamplesModalProps> = ({
  studyApiId,
  reservedSampleApiIds,
  groupMap,
  sampleTypeMap,
  metaFields,
  sampleDefinitionMap,
  closeModal,
  onSuccess,
}) => {
  const [selectedReservedSampleIds, setSelectedReservedSampleIds] = useState<Set<ReservedSampleApiId>>(
    () => new Set(reservedSampleApiIds)
  );

  const toggleReservedSampleIds = useCallback((ids: Array<ReservedSampleApiId>) => {
    setSelectedReservedSampleIds((prev) => toggleSetIds(prev, ids));
  }, []);

  const [samplesLoaded, setSamplesLoaded] = useState(false);

  const handleSamplesLoaded = useCallback((samples: Array<ReservedSample>) => {
    setSamplesLoaded(true);
    const alreadyPlannedIds = samples.reduce<Set<ReservedSampleApiId>>((acc, s) => {
      if (s.status === 'planned') {
        acc.add(s.api_id);
      }
      return acc;
    }, new Set());
    if (alreadyPlannedIds.size > 0) {
      setSelectedReservedSampleIds((prev) => {
        const next = new Set(prev);
        alreadyPlannedIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, []);

  const totalSamplesToCreate = selectedReservedSampleIds.size;

  const { loading: isCreating, invoke: executeCreate } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/reserved-samples/execute',
    path: { studyApiId },
    options: {
      invokeOnInit: false,
      onError: { toast: false, throw: true, capture: false, slug: 'reserved-planned-samples-create' },
    },
  });

  const handleCreate = useCallback(async () => {
    if (selectedReservedSampleIds.size === 0) {
      errorToast('Please select at least one sample to create planned samples for.');
      return;
    }
    try {
      await executeCreate({
        path: { studyApiId },
        body: {
          reserved_sample_api_ids: [...selectedReservedSampleIds],
          date: null,
          comments: null,
        },
      });
      successToast('Planned samples created successfully.');
      onSuccess();
      closeModal();
    } catch (err) {
      errorToast('Failed to create planned samples.');
      ExceptionHandler.captureException(
        new InVivoError('Failed to create reserved planned samples', {
          cause: err,
          slug: 'reserved-planned-samples-create',
        })
      );
    }
  }, [studyApiId, selectedReservedSampleIds, executeCreate, onSuccess, closeModal]);

  return (
    <ModalContainer size="wide">
      <ModalHeader
        title="Create planned samples"
        subText={
          samplesLoaded ? (
            <>
              Create planned samples for {totalSamplesToCreate} selected reserved sample
              {totalSamplesToCreate === 1 ? '' : 's'}.
            </>
          ) : undefined
        }
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
      />
      {samplesLoaded && totalSamplesToCreate > 0 && (
        <div className="pl4 mt3">
          <Banner info className="mw6 mr4 mb1" dismiss={false}>
            <p className="lh-copy f6">
              <strong>
                {totalSamplesToCreate} planned sample{totalSamplesToCreate === 1 ? '' : 's'}
              </strong>{' '}
              will be created upon submission.
            </p>
          </Banner>
        </div>
      )}
      <div className="mh4 mt3">
        <div className="mb3">
          <ReservedSamplesFlatTable
            studyApiId={studyApiId}
            groupMap={groupMap}
            sampleTypeMap={sampleTypeMap}
            metaFields={metaFields}
            sampleDefinitionMap={sampleDefinitionMap}
            selectedReservedSampleIds={selectedReservedSampleIds}
            toggleReservedSampleIds={toggleReservedSampleIds}
            reservedSampleApiIds={reservedSampleApiIds}
            filterCollected
            disablePlanned
            hideBorder={false}
            onSamplesLoaded={handleSamplesLoaded}
          />
        </div>
        <div className="flex flex-row justify-start gap-h-3 pt3 pb2 mt3 bt">
          <Button
            primary
            onClick={handleCreate}
            disabled={!samplesLoaded || isCreating || totalSamplesToCreate === 0}
            testId="create-reserved-planned-samples-button"
          >
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
          <Button
            plain
            onClick={closeModal}
            disabled={isCreating}
            testId="cancel-create-reserved-planned-samples-button"
          >
            Cancel
          </Button>
        </div>
      </div>
    </ModalContainer>
  );
};
